import { Box, Button, Divider, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslationContext } from '../../../context/TranslationContext';
import GoogleMapStandalone from './components/GoogleMap/GoogleMapStandalone';
import type { LocationData } from './types';

interface LocationSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelectLocation: (address: string, lat: string, lng: string) => void;
  initialAddress?: string;
}

const LocationSelectorModal = ({
  open,
  onClose,
  onSelectLocation,
  initialAddress,
}: LocationSelectorModalProps) => {
  const t = useTranslationContext();
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Resetear cuando se cierra
  useEffect(() => {
    if (!open) {
      setSelectedLocation(null);
    }
  }, [open]);

  // Posicionar el scroll al centro
  useEffect(() => {
    if (mapContainerRef.current && selectedLocation) {
      mapContainerRef.current.scrollTop = mapContainerRef.current.scrollHeight / 4;
    }
  }, [selectedLocation]);

  const handleLocationChange = useCallback((location: LocationData) => {
    setSelectedLocation(location);
  }, []);

  const handleConfirmLocation = useCallback(() => {
    if (selectedLocation) {
      onSelectLocation(
        selectedLocation.title || `${selectedLocation.lat}, ${selectedLocation.lng}`,
        selectedLocation.lat,
        selectedLocation.lng,
      );
      onClose();
    }
  }, [selectedLocation, onSelectLocation, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          height: '80vh',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          //@ts-ignore
          backgroundColor: (theme) => theme.palette.background.panel,
        },
      }}
    >
      {/* Header con título y botón cerrar */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Typography variant="body1" sx={{ flex: 1 }}>
          {t('global.selectLocation', 'Seleccionar ubicación')}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
        >
          <Box
            ref={mapContainerRef}
            sx={{
              width: '100%',
              flex: 1,
              overflow: 'auto',
              zIndex: 1,
            }}
          >
            <GoogleMapStandalone
              onLocationChange={handleLocationChange}
              initialLocation={
                initialAddress
                  ? undefined // Si hay una dirección inicial, dejar que GoogleMapStandalone la geocodifique
                  : undefined
              }
            />
          </Box>
          <Divider
            sx={{
              width: '100%',
              padding: 0,
            }}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '.5rem',
              p: 2,
            }}
          >
            <Button
              variant="outlined"
              color="error"
              size="small"
              sx={{
                borderRadius: '0.5rem',
              }}
              onClick={onClose}
            >
              {t('global.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              sx={{
                borderRadius: '0.5rem',
              }}
              onClick={handleConfirmLocation}
              disabled={!selectedLocation}
            >
              {t('global.selectLocation', 'Seleccionar ubicación')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LocationSelectorModal;
